// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { buttonClasses } from '@mui/material/Button';
import { rem } from '../../tokens/conversions';
import { typography } from '../../tokens/typography';
import { buttonColors } from './buttonColors';
export var Button = function(mode) {
    var c = buttonColors(mode);
    var _obj, _obj1, _obj2, _obj3, _obj4, _obj5, _obj6, _obj7, _obj8, _obj9, _obj10, _obj11, _obj12;
    var override = {
        MuiButton: {
            styleOverrides: {
                root: _object_spread_props(_object_spread({
                    textTransform: 'none',
                    borderRadius: rem(8),
                    border: "".concat(rem(1), " solid transparent"),
                    padding: "".concat(rem(8), " ").concat(rem(12)),
                    minHeight: rem(36),
                    minWidth: rem(36)
                }, typography.action1), (_obj = {
                    gap: rem(8),
                    // Override MUI's ButtonBase default, which also transitions `color` —
                    // text/icon color should switch instantly between states, not fade.
                    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)'
                }, _define_property(_obj, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    margin: 0
                }), _define_property(_obj, '&:focus-visible, &.Mui-focusVisible', {
                    outline: "".concat(rem(2), " solid ").concat(c.focus.border),
                    outlineOffset: rem(2)
                }), _obj)),
                sizeSmall: _object_spread_props(_object_spread({
                    padding: "".concat(rem(4), " ").concat(rem(8))
                }, typography.action2), {
                    minHeight: rem(28),
                    minWidth: rem(28),
                    gap: rem(8)
                }),
                sizeLarge: _object_spread_props(_object_spread({
                    padding: "".concat(rem(12), " ").concat(rem(24))
                }, typography.actionLarge), {
                    minHeight: rem(48),
                    minWidth: rem(48),
                    gap: rem(12)
                }),
                iconSizeSmall: {
                    '& > *:first-of-type': {
                        fontSize: rem(16)
                    }
                },
                iconSizeMedium: {
                    '& > *:first-of-type': {
                        fontSize: rem(20)
                    }
                },
                iconSizeLarge: {
                    '& > *:first-of-type': {
                        fontSize: rem(24)
                    }
                },
                containedPrimary: (_obj1 = {
                    backgroundColor: c.primary.default.background,
                    color: c.primary.default.text
                }, _define_property(_obj1, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.primary.default.icon
                }), _define_property(_obj1, '&:hover, &.pseudo-hover', _define_property({
                    backgroundColor: c.primary.hover.background,
                    color: c.primary.hover.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.primary.hover.icon
                })), _define_property(_obj1, '&:active, &.pseudo-active', _define_property({
                    backgroundColor: c.primary.active.background,
                    color: c.primary.active.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.primary.active.icon
                })), _define_property(_obj1, "&&&.".concat(buttonClasses.disabled), _define_property({
                    backgroundColor: c.primary.disabled.background,
                    color: c.primary.disabled.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.primary.disabled.icon
                })), _obj1),
                outlinedPrimary: (_obj2 = {
                    border: "".concat(rem(1), " solid ").concat(c.secondary.default.border),
                    color: c.secondary.default.text
                }, _define_property(_obj2, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.secondary.default.icon
                }), _define_property(_obj2, '&:hover, &.pseudo-hover', {
                    border: "".concat(rem(1), " solid ").concat(c.secondary.default.border),
                    backgroundColor: c.secondary.hover.background
                }), // Triple-boost: needed to beat MuiButtonGroupRoot's own borderRightColor/
                // borderBottomColor rules on firstButton/middleButton, which include an
                // unconditional variant (specificity 2) AND a `:hover` variant
                // (specificity 3, still matches while pressed since hover+active can both
                // be true) — a double-boost (specificity 3) ties with the hover variant
                // and the tie-break isn't reliable across orientations.
                _define_property(_obj2, '&&&:active, &&&.pseudo-active', _define_property({
                    border: "".concat(rem(1), " solid ").concat(c.secondary.active.border),
                    backgroundColor: c.secondary.active.background,
                    color: c.secondary.active.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.secondary.active.icon
                })), _define_property(_obj2, "&&&.".concat(buttonClasses.disabled), _define_property({
                    color: c.secondary.disabled.text,
                    border: "".concat(rem(1), " solid ").concat(c.secondary.disabled.border)
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.secondary.disabled.icon
                })), _obj2),
                textPrimary: (_obj3 = {
                    color: c.text.default.text
                }, _define_property(_obj3, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.text.default.icon
                }), _define_property(_obj3, '&:hover, &.pseudo-hover', {
                    backgroundColor: c.text.hover.background
                }), _define_property(_obj3, '&:active, &.pseudo-active', _define_property({
                    color: c.text.active.text,
                    backgroundColor: c.text.active.background
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.text.active.icon
                })), _define_property(_obj3, "&&&.".concat(buttonClasses.disabled), _define_property({
                    color: c.text.disabled.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.text.disabled.icon
                })), _obj3),
                outlinedSecondary: (_obj4 = {
                    border: "".concat(rem(1), " solid ").concat(c.invertedOutlined.default.border),
                    color: c.invertedOutlined.default.text
                }, _define_property(_obj4, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedOutlined.default.icon
                }), _define_property(_obj4, '&:hover, &.pseudo-hover', {
                    border: "".concat(rem(1), " solid ").concat(c.invertedOutlined.hover.border),
                    backgroundColor: c.invertedOutlined.hover.background
                }), // Triple-boost: see outlinedPrimary's `&&&:active` above.
                _define_property(_obj4, '&&&:active, &&&.pseudo-active', _define_property({
                    border: "".concat(rem(1), " solid ").concat(c.invertedOutlined.active.border),
                    backgroundColor: c.invertedOutlined.active.background,
                    color: c.invertedOutlined.active.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedOutlined.active.icon
                })), _define_property(_obj4, "&&&.".concat(buttonClasses.disabled), _define_property({
                    border: "".concat(rem(1), " solid ").concat(c.invertedOutlined.disabled.border),
                    color: c.invertedOutlined.disabled.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedOutlined.disabled.icon
                })), _obj4),
                textSecondary: (_obj5 = {
                    color: c.invertedText.default.text
                }, _define_property(_obj5, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedText.default.icon
                }), _define_property(_obj5, '&:hover, &.pseudo-hover', {
                    backgroundColor: c.invertedText.hover.background
                }), _define_property(_obj5, '&:active, &.pseudo-active', _define_property({
                    backgroundColor: c.invertedText.active.background,
                    color: c.invertedText.active.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedText.active.icon
                })), _define_property(_obj5, "&&&.".concat(buttonClasses.disabled), _define_property({
                    color: c.invertedText.disabled.text
                }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                    color: c.invertedText.disabled.icon
                })), _obj5)
            },
            variants: [
                {
                    props: {
                        size: 'extraSmall'
                    },
                    style: _object_spread_props(_object_spread({
                        padding: "".concat(rem(2), " ").concat(rem(4))
                    }, typography.actionTiny), _define_property({
                        minHeight: rem(20),
                        minWidth: rem(28),
                        borderRadius: rem(4),
                        gap: rem(4)
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        '& > *:first-of-type': {
                            fontSize: rem(12)
                        }
                    }))
                },
                {
                    props: {
                        variant: 'destructive'
                    },
                    style: (_obj6 = {
                        backgroundColor: c.destructive.default.background,
                        color: c.destructive.default.text
                    }, _define_property(_obj6, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.destructive.default.icon
                    }), _define_property(_obj6, '&:hover, &.pseudo-hover', {
                        backgroundColor: c.destructive.hover.background
                    }), _define_property(_obj6, '&:active, &.pseudo-active', {
                        backgroundColor: c.destructive.active.background
                    }), _define_property(_obj6, "&&&.".concat(buttonClasses.disabled), _define_property({
                        backgroundColor: c.destructive.disabled.background,
                        color: c.destructive.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.destructive.disabled.icon
                    })), _obj6)
                },
                {
                    props: {
                        variant: 'translucent'
                    },
                    style: (_obj7 = {
                        backgroundColor: c.translucent.default.background,
                        color: c.translucent.default.text
                    }, _define_property(_obj7, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.translucent.default.icon
                    }), _define_property(_obj7, '&:hover, &.pseudo-hover', {
                        backgroundColor: c.translucent.hover.background
                    }), _define_property(_obj7, '&:active, &.pseudo-active', {
                        backgroundColor: c.translucent.active.background
                    }), _define_property(_obj7, "&&&.".concat(buttonClasses.disabled), _define_property({
                        backgroundColor: c.translucent.disabled.background,
                        color: c.translucent.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.translucent.disabled.icon
                    })), _obj7)
                },
                {
                    props: {
                        variant: 'invertedContained'
                    },
                    style: (_obj8 = {
                        backgroundColor: c.invertedContained.default.background,
                        color: c.invertedContained.default.text
                    }, _define_property(_obj8, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.invertedContained.default.icon
                    }), _define_property(_obj8, '&:hover', _define_property({
                        backgroundColor: c.invertedContained.hover.background,
                        color: c.invertedContained.hover.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.invertedContained.hover.icon
                    })), _define_property(_obj8, '&:active', _define_property({
                        backgroundColor: c.invertedContained.active.background,
                        color: c.invertedContained.active.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.invertedContained.active.icon
                    })), _define_property(_obj8, "&&&.".concat(buttonClasses.disabled), _define_property({
                        backgroundColor: c.invertedContained.disabled.background,
                        color: c.invertedContained.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.invertedContained.disabled.icon
                    })), _obj8)
                },
                {
                    // Figma: Button/Secondary/Contained/* — grey-color contained variant,
                    // distinct from `contained`/color=primary. Default/hover/active backgrounds
                    // are gradients (see buttonColors.ts's secondaryContained), so `background`
                    // is used instead of `backgroundColor`. `border: 'none'` overrides root's
                    // shared `1px solid transparent` — Chrome renders a visible seam where a
                    // transparent border meets a linear-gradient background at a rounded corner,
                    // and Figma specifies no border for this variant anyway.
                    props: {
                        variant: 'secondaryContained'
                    },
                    style: (_obj9 = {
                        border: 'none',
                        background: c.secondaryContained.default.background,
                        color: c.secondaryContained.default.text
                    }, _define_property(_obj9, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryContained.default.icon
                    }), _define_property(_obj9, '&:hover, &.pseudo-hover', {
                        background: c.secondaryContained.hover.background
                    }), _define_property(_obj9, '&:active, &.pseudo-active', _define_property({
                        background: c.secondaryContained.active.background,
                        color: c.secondaryContained.active.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryContained.active.icon
                    })), _define_property(_obj9, "&&&.".concat(buttonClasses.disabled), _define_property({
                        background: c.secondaryContained.disabled.background,
                        color: c.secondaryContained.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryContained.disabled.icon
                    })), _obj9)
                },
                {
                    // Figma: Button/Secondary/Outlined/* — grey-color outlined variant, distinct
                    // from `outlined`/color=primary. Default/hover/active backgrounds are
                    // gradients (see buttonColors.ts's secondaryOutlined).
                    props: {
                        variant: 'secondaryOutlined'
                    },
                    style: (_obj10 = {
                        background: c.secondaryOutlined.default.background,
                        border: "".concat(rem(1), " solid ").concat(c.secondaryOutlined.default.border),
                        color: c.secondaryOutlined.default.text
                    }, _define_property(_obj10, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryOutlined.default.icon
                    }), _define_property(_obj10, '&:hover, &.pseudo-hover', {
                        background: c.secondaryOutlined.hover.background
                    }), // Triple-boost: see outlinedPrimary's `&&&:active` above.
                    _define_property(_obj10, '&&&:active, &&&.pseudo-active', _define_property({
                        background: c.secondaryOutlined.active.background,
                        border: "".concat(rem(1), " solid ").concat(c.secondaryOutlined.active.border),
                        color: c.secondaryOutlined.active.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryOutlined.active.icon
                    })), _define_property(_obj10, "&&&.".concat(buttonClasses.disabled), _define_property({
                        border: "".concat(rem(1), " solid ").concat(c.secondaryOutlined.disabled.border),
                        color: c.secondaryOutlined.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryOutlined.disabled.icon
                    })), _obj10)
                },
                {
                    // Figma: Button/Secondary/Text/* — grey-color text variant, distinct from
                    // `text`/color=primary. Hover/active backgrounds are gradients (see
                    // buttonColors.ts's secondaryText). `border: 'none'` overrides root's shared
                    // `1px solid transparent` — see secondaryContained's comment for why a
                    // transparent border seams visibly against a gradient background.
                    props: {
                        variant: 'secondaryText'
                    },
                    style: (_obj11 = {
                        border: 'none',
                        color: c.secondaryText.default.text
                    }, _define_property(_obj11, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryText.default.icon
                    }), _define_property(_obj11, '&:hover, &.pseudo-hover', {
                        background: c.secondaryText.hover.background
                    }), _define_property(_obj11, '&:active, &.pseudo-active', _define_property({
                        background: c.secondaryText.active.background,
                        color: c.secondaryText.active.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryText.active.icon
                    })), _define_property(_obj11, "&&&.".concat(buttonClasses.disabled), _define_property({
                        color: c.secondaryText.disabled.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondaryText.disabled.icon
                    })), _obj11)
                },
                {
                    // Filter uses the exact same styles as the outlined button (`c.secondary.*`,
                    // same tokens as the `outlinedPrimary` slot above).
                    props: {
                        variant: 'filter'
                    },
                    style: (_obj12 = {
                        border: "".concat(rem(1), " solid ").concat(c.secondary.default.border),
                        color: c.secondary.default.text
                    }, _define_property(_obj12, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondary.default.icon
                    }), _define_property(_obj12, '&:hover, &.pseudo-hover', {
                        border: "".concat(rem(1), " solid ").concat(c.secondary.default.border),
                        backgroundColor: c.secondary.hover.background
                    }), _define_property(_obj12, '&&&:active, &&&.pseudo-active', _define_property({
                        border: "".concat(rem(1), " solid ").concat(c.secondary.active.border),
                        backgroundColor: c.secondary.active.background,
                        color: c.secondary.active.text
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondary.active.icon
                    })), _define_property(_obj12, "&&&.".concat(buttonClasses.disabled), _define_property({
                        color: c.secondary.disabled.text,
                        border: "".concat(rem(1), " solid ").concat(c.secondary.disabled.border)
                    }, "& .".concat(buttonClasses.startIcon, ", & .").concat(buttonClasses.endIcon), {
                        color: c.secondary.disabled.icon
                    })), _obj12)
                }
            ],
            defaultProps: {
                disableElevation: true,
                color: 'primary',
                disableTouchRipple: true
            }
        }
    };
    return override;
};

//# sourceMappingURL=Button.js.map